import { BookingAddressPicker } from '@components/Address/BookingAddressPicker';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { BookingAddress, NoddiAsyncError } from 'noddi-types';
import { NoddiInfoBanner, NoddiTextArea } from 'noddi-ui-web';

type Props = {
  /** A customer without bookings has no address on file, so they pick one to route the request by. */
  askForAddress: boolean;
  selectedAddress: BookingAddress | undefined;
  onSelectAddress: (address: BookingAddress | undefined) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  /** Car and season pairs a previous submit did not get through; empty until a submit partly fails. */
  unsentRequests: { key: string; value: string }[];
  /** Why the last submit failed; the customer app raises no toasts, so this is the only place it shows. */
  submitError: NoddiAsyncError | null;
};

export const DetailsStep = ({
  askForAddress,
  selectedAddress,
  onSelectAddress,
  description,
  onDescriptionChange,
  unsentRequests,
  submitError
}: Props) => (
  <>
    {askForAddress && (
      <div className='flex flex-col gap-2'>
        <p className='font-semibold'>{t`Where do you need our help?`}</p>
        <BookingAddressPicker
          hideServiceAreaCheck
          setSelectedAddress={onSelectAddress}
          selectedAddress={selectedAddress}
        />
      </div>
    )}
    <NoddiTextArea
      className='bg-white'
      label={t`Anything more we should know?`}
      value={description}
      onChange={(e) => onDescriptionChange(e.target.value)}
    />
    <p>
      <strong>NB!</strong>{' '}
      <Trans>
        Submitting your information is non-binding and comes with no obligation. It is only used for us to prepare and
        send you an offer.
      </Trans>
    </p>
    {unsentRequests.length > 0 && (
      <NoddiInfoBanner
        variant='warning'
        title={t`Some of your requests were not sent. Submit again to send the rest.`}
        texts={unsentRequests}
      />
    )}
    {submitError && <ApiErrorMessageWithTrans error={submitError} />}
  </>
);
